import type { HttpValidationError } from "@common/client"
import {
  infiniteQueryOptions,
  type MutationKey,
  type MutationScope,
  type QueryKey,
  type UseQueryOptions,
  type UseSuspenseQueryOptions,
  useMutation,
  useQueryClient,
} from "@tanstack/react-query"
import { AxiosError, type AxiosResponse } from "axios"

type AxiosShape<T, E> = AxiosResponse<T> | AxiosError<E>

function parsedJson(text: string): unknown {
  try {
    return JSON.parse(text)
  } catch {
    return undefined
  }
}

export function parseBlobErrors<I, R extends { error: unknown }>(
  serviceCall: (input: I) => Promise<R>,
): (input: I) => Promise<R> {
  return async (input) => {
    const result = await serviceCall(input)
    if (!(result instanceof AxiosError && result.error instanceof Blob)) {
      return result
    }
    const detail = parsedJson(await result.error.text())
    return detail === undefined
      ? result
      : Object.assign(result, { error: detail })
  }
}

// turns an axios success or error tuple thing into a promise
export function unwrap<O, E>(data: AxiosShape<O, E>): Promise<O> {
  if (data instanceof AxiosError) {
    return Promise.reject(data)
  }
  return Promise.resolve(data.data)
}

/**
 * This sad wrapper adapts auto-gen Axios service methods to useQuery
 * it handles unwrapping the data / error return type to avoid | undefineds
 */
export function makeQuery<
  O,
  E = AxiosError<HttpValidationError>,
  I = undefined,
>({
  serviceCall,
  queryKey,
  ...opts
}: Omit<UseQueryOptions<O, E>, "queryKey"> & {
  serviceCall: (input: I) => Promise<AxiosShape<O, E>>
  queryKey: (input: I) => QueryKey
}): (input: I) => UseSuspenseQueryOptions<O, E> {
  return (input) => ({
    ...opts,
    queryKey: queryKey(input),
    queryFn: () => serviceCall(input).then(unwrap),
  })
}

type PageRange = { skip: number; limit: number }

export function makeInfiniteQuery<
  O extends { count: number },
  I,
  E = AxiosError<HttpValidationError>,
>({
  serviceCall,
  queryKey,
  pageSize,
}: {
  serviceCall: (input: I, page: PageRange) => Promise<AxiosShape<O, E>>
  queryKey: (input: I) => QueryKey
  pageSize: number
}) {
  return (input: I) =>
    infiniteQueryOptions({
      queryKey: queryKey(input),
      queryFn: ({ pageParam }: { pageParam: number }) =>
        serviceCall(input, { skip: pageParam, limit: pageSize }).then(unwrap),
      initialPageParam: 0,
      getNextPageParam: (lastPage: O, allPages: O[]) => {
        const loaded = allPages.length * pageSize
        return loaded < lastPage.count ? loaded : undefined
      },
    })
}

export type GroupPages<I, O, E> = {
  pageSize: number
  groups: (input: I) => UseSuspenseQueryOptions<O, E>
  page: (
    input: I,
    groupKey: string,
    index: number,
  ) => UseQueryOptions<O, E, O, QueryKey>
}

type GroupPageRange = PageRange & {
  group_key: string | null
  group_limit: number | null
}

export function makeGroupPages<
  O extends { count: number },
  I,
  E = AxiosError<HttpValidationError>,
>({
  serviceCall,
  queryKey,
  pageSize,
}: {
  serviceCall: (input: I, page: GroupPageRange) => Promise<AxiosShape<O, E>>
  queryKey: (input: I) => QueryKey
  pageSize: number
}): GroupPages<I, O, E> {
  return {
    pageSize,
    groups: (input) => ({
      queryKey: [...queryKey(input), "groups"],
      queryFn: () =>
        serviceCall(input, {
          skip: 0,
          limit: 0,
          group_key: null,
          group_limit: pageSize,
        }).then(unwrap),
    }),
    page: (input, groupKey, index) => ({
      queryKey: [...queryKey(input), "group", groupKey, index],
      queryFn: () =>
        serviceCall(input, {
          skip: index * pageSize,
          limit: pageSize,
          group_key: groupKey,
          group_limit: null,
        }).then(unwrap),
    }),
  }
}

export async function ensureListData({
  grouped,
  groups,
  flat,
}: {
  grouped: boolean
  groups: () => Promise<{ groups: unknown[] }>
  flat: () => Promise<unknown>
}): Promise<void> {
  if (grouped && (await groups()).groups.length > 0) return
  await flat()
}

export function pagedRows<R>(
  pages: { data: R[] }[],
  keyOf: (row: R) => string,
): R[] {
  const seen = new Set<string>()
  return pages
    .flatMap((page) => page.data)
    .filter((row) => {
      const key = keyOf(row)
      if (seen.has(key)) return false
      seen.add(key)
      return true
    })
}

/**
 * You can do some type astronautics to handle arity differences
 * but its so horrible I was like lets just have two fns
 */
export function makeNoArgsQuery<O, E>({
  serviceCall,
  queryKey,
  ...opts
}: Pick<UseQueryOptions<O, E>, "staleTime"> & {
  serviceCall: () => Promise<AxiosShape<O, E>>
  queryKey: QueryKey
}): () => UseSuspenseQueryOptions<O, E> {
  return () => ({
    ...opts,
    queryKey,
    queryFn: () => serviceCall().then(unwrap),
  })
}

/**
 * Like makeQuery above
 * mildly simplifies invalidations
 */
export function makeAxiosMutation<I, O, E = AxiosError<HttpValidationError>>({
  serviceCall,
  invalidates,
  mutationKey,
  scope,
}: {
  serviceCall: (input: I) => Promise<AxiosShape<O, E>>
  invalidates: (input: I, data: O) => QueryKey[]
  mutationKey?: MutationKey
  scope?: MutationScope
}) {
  return () => {
    const queryClient = useQueryClient()

    return useMutation<O, E, I>({
      mutationKey,
      scope,
      mutationFn: (data: I) => serviceCall(data).then(unwrap),
      onSuccess: (data: O, variables: I) =>
        Promise.all(
          invalidates(variables, data).map((queryKey) =>
            queryClient.invalidateQueries({ queryKey }),
          ),
        ),
    })
  }
}

export function makeDynamicAxiosMutation<I, O, E, P = undefined>({
  serviceCall,
  invalidates,
}: {
  serviceCall: (input: I) => Promise<AxiosShape<O, E>>
  invalidates: (input: I, param: P) => QueryKey[]
}) {
  return (param: P) => {
    const queryClient = useQueryClient()

    return useMutation<O, E | Error, I>({
      mutationFn: (data: I) => serviceCall(data).then(unwrap),
      onSuccess: (_data: O, variables: I) => {
        invalidates(variables, param).forEach((queryKey) =>
          queryClient.invalidateQueries({ queryKey }),
        )
      },
    })
  }
}
